import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { LuHandshake } from "react-icons/lu";
import { PageHeader } from "@/components/ui/page-header";

interface Sponsor {
  id: string;
  stripePaymentId: string;
  name: string | null;
  email: string | null;
  isAnonymous: boolean;
  amount: number;
  currency: string;
  showAmount: boolean;
  message: string | null;
  donatedAt: string;
}

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

export function SponsorsPage() {
  const queryClient = useQueryClient();
  const sponsorsQuery = useQuery({
    queryKey: ["console", "sponsors"],
    queryFn: () => api.sponsors.list({ limit: 200 }),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof api.sponsors.update>[1];
    }) => api.sponsors.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["console", "sponsors"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const columns: Column<Sponsor>[] = [
    {
      header: "Name",
      cell: (s) =>
        s.isAnonymous ? (
          <em className="text-[var(--color-text-subtle)]">Anonymous</em>
        ) : (
          (s.name ?? "—")
        ),
    },
    { header: "Email", cell: (s) => s.email ?? "—", className: "text-[var(--color-text-muted)]" },
    {
      header: "Amount",
      cell: (s) => formatMoney(s.amount, s.currency),
    },
    {
      header: "Show amount publicly",
      cell: (s) => (
        <input
          type="checkbox"
          checked={s.showAmount}
          onChange={(e) =>
            updateMutation.mutate({
              id: s.id,
              body: { showAmount: e.target.checked },
            })
          }
        />
      ),
    },
    {
      header: "Anonymous",
      cell: (s) => (
        <input
          type="checkbox"
          checked={s.isAnonymous}
          onChange={(e) =>
            updateMutation.mutate({
              id: s.id,
              body: { isAnonymous: e.target.checked },
            })
          }
        />
      ),
    },
    { header: "Message", cell: (s) => s.message ?? "—", className: "text-[var(--color-text-muted)] max-w-xs truncate" },
    {
      header: "When",
      cell: (s) => new Date(s.donatedAt).toLocaleDateString(),
      className: "text-[var(--color-text-muted)] whitespace-nowrap",
    },
  ];

  return (
    <>
      <PageHeader
        icon={LuHandshake}
        title="Sponsors"
        description={
          sponsorsQuery.data
            ? `${sponsorsQuery.data.total} sponsors · ${formatMoney(sponsorsQuery.data.totalAmountCents, "usd")} total`
            : undefined
        }
      />
      <div className="p-8">
        <Card>
          <DataTable
            columns={columns}
            rows={sponsorsQuery.data?.sponsors}
            isLoading={sponsorsQuery.isLoading}
            isError={sponsorsQuery.isError}
            rowKey={(s) => s.id}
            emptyMessage="No sponsors yet."
          />
        </Card>
      </div>
    </>
  );
}
